/**
 * Handle Icon Mapping System
 *
 * 3-level fallback hierarchy:
 * 1. Handle name (most specific) - e.g., "negative_prompt" → "ban"
 * 2. Handle type (fallback) - e.g., "text" → "type"
 * 3. Default (last resort) - "circle"
 *
 * Icons are static strings (no dynamic imports) for performance.
 * All icons must exist in the Icon component.
 */

/**
 * Handle icon mapping
 * Organized by priority: name-specific → type-specific → default
 */
export const HANDLE_ICONS = {
  // ============================================================================
  // LEVEL 1: Handle Name (Most Specific)
  // ============================================================================
  byName: {
    // Text-related handles
    'negative_prompt': 'ban',
    'prompt': 'message-square',
    'system_prompt': 'settings',
    'context': 'file-text',

    // Image-related handles
    'input_image': 'image',
    'reference_image': 'image-plus',
    'style_reference_image_url': 'image-plus',
    'mask': 'circle-dashed',

    // Special handles
    'seed': 'hash',
    'style': 'palette',
    'model': 'cpu',

    // LoRA-related handles
    'loras-input': 'sparkles',
    'loras-output': 'sparkles',
    'loras': 'sparkles',
  } as Record<string, string>,

  // ============================================================================
  // LEVEL 2: Handle Type (Fallback)
  // ============================================================================
  byType: {
    'text': 'type',
    'image': 'image',
    'video': 'video',
    'audio': 'music',
    'mask': 'circle-dashed',
    'file': 'file',
    '3d': 'box',
    'code': 'code',
    'number': 'hash',
    'boolean': 'check-square',
    'lora': 'sparkles',
    'any': 'circle',
  } as Record<string, string>,

  // ============================================================================
  // LEVEL 3: Default (Last Resort)
  // ============================================================================
  default: 'circle' as string,
} as const;

/**
 * Get icon name for a handle
 *
 * Priority:
 * 1. Check handle name (e.g., "negative_prompt")
 * 2. Check handle type (e.g., "text")
 * 3. Return default ("circle")
 *
 * @param handleName - Parameter name (e.g., "prompt", "negative_prompt")
 * @param handleType - Handle type (e.g., "text", "image", "video")
 * @returns Icon name for Icon component
 *
 * @example
 * getHandleIcon('negative_prompt', 'text') // → 'ban'
 * getHandleIcon('prompt', 'text')          // → 'message-square'
 * getHandleIcon('unknown', 'text')         // → 'type'
 * getHandleIcon('unknown', 'unknown')      // → 'circle'
 */
export function getHandleIcon(handleName?: string, handleType?: string): string {
  // Level 1: Check by name (most specific)
  if (handleName && HANDLE_ICONS.byName[handleName]) {
    return HANDLE_ICONS.byName[handleName];
  }

  // Level 2: Check by type (fallback)
  if (handleType && HANDLE_ICONS.byType[handleType]) {
    return HANDLE_ICONS.byType[handleType];
  }

  // Level 3: Default (last resort)
  return HANDLE_ICONS.default;
}

/**
 * Register custom handle icon for a specific parameter name
 *
 * @example
 * registerHandleIcon('my_custom_param', 'sparkles')
 */
export function registerHandleIcon(handleName: string, iconName: string): void {
  HANDLE_ICONS.byName[handleName] = iconName;
}

/**
 * Get all registered handle name icons
 * Useful for debugging or admin panels
 */
export function getRegisteredHandleIcons(): Record<string, string> {
  return { ...HANDLE_ICONS.byName };
}
